import axios from 'axios';
import { baseURL, generalEndpoint } from '../config';

const API_BASE_URL = process.env.REACT_APP_API_URL || baseURL + '/' + generalEndpoint;

/**
 * Get all strategies
 * @param {string} token - Authentication token
 * @returns {Promise<Object>} The API response
 */
export const getAllStrategies = async (token) => {
    const config = {
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'Accept': 'application/json'
        },
    };

    const startTime = Date.now();

    try {
        const response = await axios.get(
            `${API_BASE_URL}/Strategy/all`,
            config
        );
        
        const endTime = Date.now();
        const duration = endTime - startTime;
        
        if (duration > 5000) {
            console.warn(`⚠️ Slow API response: ${duration}ms for /Strategy/all`);
        }
        
        return response.data;
    } catch (error) {
        const endTime = Date.now();
        const duration = endTime - startTime;
        
        if (error.code === 'ECONNABORTED') {
            throw new Error(`Request timeout after ${duration}ms. The server is taking too long to respond.`);
        }
        
        throw error.response?.data || error.message;
    }
};

/**
 * Add a new Strategy
 * @param {string} token - Authentication token
 * @param {Object} strategyData - Strategy data object
 * @returns {Promise<Object>} The API response
 */
export const addStrategy = async (token, strategyData) => {
    const config = {
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
        }
    };

    try {
        console.log('API Request - URL:', `${API_BASE_URL}/Strategy/create`);
        console.log('API Request - Data:', strategyData);
        
        const response = await axios.post(
            `${API_BASE_URL}/Strategy/create`,
            strategyData,
            config
        );
        
        
        // Handle successful creation (201 Created)
        if (response.status === 201) {
            console.log('Strategy created successfully (201 Created)');
            return { 
                success: true, 
                message: 'Strategy created successfully',
                data: response.data 
            };
        }
        
        return response.data;
    } catch (error) {
        console.error('Error adding strategy:', error);
        throw error.response?.data || error.message;
    }
};

/**
 * Update an existing Strategy
 * @param {string} token - Authentication token
 * @param {number} strategyId - Strategy ID to update
 * @param {Object} strategyData - Strategy data object
 * @returns {Promise<Object>} The API response
 */
export const updateStrategy = async (token, strategyId, strategyData) => {
    const config = {
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
        }
    };

    try {
        console.log('API Request - URL:', `${API_BASE_URL}/Strategy/${strategyId}`);
        console.log('API Request - Data:', strategyData);
        
        const response = await axios.put(
            `${API_BASE_URL}/Strategy/${strategyId}`,
            strategyData,
            config
        );
        
        
        // Handle 204 No Content response (successful update with no response body)
        if (response.status === 204) {
            console.log('Strategy updated successfully (204 No Content)');
            return { success: true, message: 'Strategy updated successfully' };
        }
        
        return response.data;
    } catch (error) {
        console.error('Error updating strategy:', error);
        console.error('Error response:', error.response);
        throw error.response?.data || error.message;
    }
};

/**
 * Delete a Strategy
 * @param {string} token - Authentication token
 * @param {number} strategyId - Strategy ID to delete
 * @returns {Promise<Object>} The API response
 */
export const deleteStrategy = async (token, strategyId) => {
    const config = {
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'Accept': 'application/json'
        }
    };

    try {
        console.log('API Request - URL:', `${API_BASE_URL}/Strategy/${strategyId}`);
        
        const response = await axios.delete(
            `${API_BASE_URL}/Strategy/${strategyId}`,
            config
        );
        
        console.log('API Response:', response.data);
        return response.data;
    } catch (error) {
        console.error('Error deleting strategy:', error);
        throw error.response?.data || error.message;
    }
};

/**
 * Start a Strategy
 * @param {string} token - Authentication token
 * @param {number} strategyId - Strategy ID to start
 * @returns {Promise<Object>} The API response
 */
export const startStrategy = async (token, strategyId) => {
    const config = {
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'Accept': 'application/json'
        }
    };

    try {
        
        const response = await axios.post(
            `${API_BASE_URL}/Strategy/start?strategyID=${strategyId}`,
            {},
            config
        );
        
        
        return {
            success: true,
            message: 'Strategy started successfully',
            data: response.data
        };
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

/**
 * Stop a Strategy
 * @param {string} token - Authentication token
 * @param {number} strategyId - Strategy ID to stop
 * @returns {Promise<Object>} The API response
 */
export const stopStrategy = async (token, strategyId) => {
    const config = {
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'Accept': 'application/json'
        }
    };

    try {
        
        const response = await axios.post(
            `${API_BASE_URL}/Strategy/stop/${strategyId}`,
            {},
            config
        );
        
        
        return {
            success: true,
            message: 'Strategy stopped successfully',
            data: response.data
        };
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

/**
 * Get all running strategies
 * @param {string} token - Authentication token
 * @returns {Promise<Object>} The API response
 */
export const getRunningStrategies = async (token) => {
    const config = {
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'Accept': 'application/json'
        }
    };

    try {
        console.log('API Request - URL:', `${API_BASE_URL}/Strategy/running`);
        
        const response = await axios.get(
            `${API_BASE_URL}/Strategy/running`,
            config
        );
        
        
        return response.data;
    } catch (error) {
        console.error('Error fetching running strategies:', error);
        throw error.response?.data || error.message;
    }
};

/**
 * Get strategy by ID
 * @param {string} token - Authentication token
 * @param {number} strategyId - Strategy ID to fetch
 * @returns {Promise<Object>} The API response
 */
export const getStrategyById = async (token, strategyId) => {
    const config = {
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'Accept': 'application/json'
        }
    };

    try {
        console.log('API Request - URL:', `${API_BASE_URL}/Strategy/${strategyId}`);

        const response = await axios.get(
            `${API_BASE_URL}/Strategy/${strategyId}`,
            config
        );

        console.log('Get Strategy By ID Response:', response.data);
        return response.data;
    } catch (error) {
        console.error('Error fetching strategy by ID:', error);
        throw error.response?.data || error.message;
    }
};

/**
 * Get strategy status by ID
 * @param {string} token - Authentication token
 * @param {number} strategyId - Strategy ID to check
 * @returns {Promise<Object>} The API response
 */
export const getStrategyStatus = async (token, strategyId) => {
    const config = {
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'Accept': 'application/json'
        }
    };

    try {

        const response = await axios.get(
            `${API_BASE_URL}/Strategy/status/${strategyId}`,
            config
        );

        // Handle the response structure - extract the data part
        const responseData = response.data;
        if (responseData && responseData.data) {
            return responseData.data; // Return the nested data object
        }
        return responseData; // Return the full response if no nested data
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

/**
 * Update strategy parameters
 * @param {string} token - Authentication token
 * @param {number} strategyId - Strategy ID to update
 * @param {Object} parameters - Parameters object with dot notation keys
 * @returns {Promise<Object>} The API response
 */
export const updateStrategyParameters = async (token, strategyId, parameters) => {
    const config = {
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
        }
    };

    try {
        console.log('API Request - URL:', `${API_BASE_URL}/Strategy/${strategyId}/parameters`);
        console.log('API Request - Parameters:', parameters);

        const response = await axios.patch(
            `${API_BASE_URL}/Strategy/${strategyId}/parameters`,
            parameters,
            config
        );

        console.log('API Response:', response);

        // Handle 204 No Content response (successful update with no response body)
        if (response.status === 204) {
            console.log('Strategy parameters updated successfully (204 No Content)');
            return { success: true, message: 'Strategy parameters updated successfully' };
        }

        // Handle 200 OK response with data
        if (response.status === 200) {
            console.log('Strategy parameters updated successfully (200 OK)');
            return {
                success: true,
                message: 'Strategy parameters updated successfully',
                data: response.data
            };
        }

        return response.data;
    } catch (error) {
        console.error('Error updating strategy parameters:', error);
        console.error('Error response:', error.response);
        throw error.response?.data || error.message;
    }
};

/**
 * Execute manual order for a strategy
 * @param {string} token - Authentication token
 * @param {number} strategyId - Strategy ID
 * @param {string} orderType - Order type (e.g., "OpenBuySpread", "CloseLastBuySpread")
 * @param {Array} orders - Array of order objects (optional, only for open operations)
 * @returns {Promise<Object>} The API response
 */
export const executeManualOrder = async (token, strategyId, orderType, orders = null) => {
    const config = {
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
        }
    };

    const requestBody = {
        orderType: orderType
    };

    // Only include orders array for open operations
    if (orders && Array.isArray(orders) && orders.length > 0) {
        requestBody.orders = orders;
    }

    try {
        console.log('Manual Order API Request - URL:', `${API_BASE_URL}/Strategy/${strategyId}/manual-order`);
        console.log('Manual Order API Request - Body:', requestBody);

        const response = await axios.post(
            `${API_BASE_URL}/Strategy/${strategyId}/manual-order`,
            requestBody,
            config
        );

        console.log('Manual Order API Response:', response.data);

        return {
            success: true,
            data: response.data
        };
    } catch (error) {
        console.error('Error executing manual order:', error);
        console.error('Error response:', error.response);

        // Extract error message from response
        const errorMessage = error.response?.data?.failureReason
            || error.response?.data?.message
            || error.message
            || 'Failed to execute manual order';

        return {
            success: false,
            error: errorMessage,
            data: error.response?.data
        };
    }
};

export const resetStrategySnapshot = async (token, strategyId) => {
    const config = {
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
        }
    };

    try {
        console.log('Reset Strategy Snapshot API Request - URL:', `${API_BASE_URL}/Strategy/${strategyId}/reset-snapshot`);

        const response = await axios.delete(
            `${API_BASE_URL}/Strategy/${strategyId}/reset-snapshot`,
            config
        );

        console.log('Reset Strategy Snapshot API Response:', response.data);

        return {
            success: true,
            data: response.data
        };
    } catch (error) {
        console.error('Error resetting strategy snapshot:', error);
        console.error('Error response:', error.response);

        const errorMessage = error.response?.data?.failureReason
            || error.response?.data?.message
            || error.message
            || 'Failed to reset strategy snapshot';

        return {
            success: false,
            error: errorMessage,
            data: error.response?.data
        };
    }
};
